// src/stores/websocketStore.js
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import config from "../config";
import logger from "../utils/logger";

/**
 * WebSocket Store - Zustand-based WebSocket connection management
 *
 * Manages:
 * - WebSocket connection lifecycle (connect, disconnect, reconnect)
 * - Connection status tracking
 * - Heartbeat/ping-pong mechanism
 * - Reconnection with exponential backoff
 * - Message handling and routing
 * - Session management
 * - Global WebSocket instance (singleton pattern)
 * - Auto-reconnect configuration
 *
 * Features:
 * - Exponential backoff reconnection strategy
 * - Heartbeat to detect silent disconnects
 * - Tab visibility handling (pause/resume reconnect)
 * - Global singleton to prevent duplicate connections
 * - Connection status persistence
 * - Message event callbacks
 *
 * Persistence:
 * - Auto-reconnect preferences (isAutoReconnectEnabled, showReconnectModal)
 * - All other state is transient (connection status, session)
 *
 * Migration Note:
 * This store replaces WebSocket-related logic from AppContent.js
 * See docs/ZUSTAND_MIGRATION_GUIDE.md for migration details
 */

// Global WebSocket instance (singleton pattern to prevent duplicate connections)
let globalWebSocket = null;
let globalSessionId = null;

// Module-level message handlers ref (prevents Zustand re-render loops)
// Using a ref object to maintain a stable reference across store updates
const messageHandlersRef = { current: [] };

// Constants
const HEARTBEAT_INTERVAL_MS = 30000; // 30 seconds
const BASE_RECONNECT_DELAY_MS = 1000; // 1 second
const MAX_RECONNECT_DELAY_MS = 30000; // 30 seconds
const MAX_RECONNECT_ATTEMPTS = 10;

const useWebSocketStore = create(
  devtools(
    persist(
      (set, get) => ({
        // --- State ---
        ws: null, // WebSocket instance reference
        sessionId: null, // Unique session identifier
        connectionStatus: "Disconnected", // Disconnected | Connecting... | Connected | Connection Error | Connection Failed
        isAutoReconnectEnabled: true, // Auto-reconnect setting (persisted)
        showReconnectModal: true, // Show reconnect modal setting (persisted)
        reconnectAttempts: 0, // Current reconnect attempt count
        reconnectDelay: BASE_RECONNECT_DELAY_MS, // Current reconnect delay
        heartbeatInterval: null, // Heartbeat interval reference
        reconnectTimeout: null, // Reconnect timeout reference
        // messageHandlers removed - now using module-level ref to avoid re-render loops
        isTabVisible: true, // Tab visibility state (for pausing reconnects)

        // --- Helper: Generate Session ID ---

        /**
         * Generate a unique UUID v4 session ID
         * @returns {string} UUID v4 string
         */
        generateSessionId: () => {
          // Use window.crypto if available, otherwise use fallback for Node.js testing
          const cryptoObj =
            typeof window !== "undefined" && window.crypto
              ? window.crypto
              : crypto;
          return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
            (
              c ^
              (cryptoObj.getRandomValues(new Uint8Array(1))[0] &
                (15 >> (c / 4)))
            ).toString(16)
          );
        },

        /**
         * Calculate exponential backoff delay with jitter
         * @returns {number} Delay in milliseconds
         */
        getReconnectDelay: () => {
          const { reconnectAttempts } = get();
          const exponentialDelay = Math.min(
            BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts),
            MAX_RECONNECT_DELAY_MS
          );
          // Add jitter (0-1000ms random) to prevent thundering herd
          const jitter = Math.random() * 1000;
          return exponentialDelay + jitter;
        },

        // --- Connection Management ---

        /**
         * Connect to WebSocket
         */
        connect: () => {
          const {
            ws,
            generateSessionId,
            reconnectTimeout,
            heartbeatInterval,
            reconnectAttempts,
          } = get();

          // Check for existing global WebSocket FIRST (singleton pattern)
          if (
            globalWebSocket &&
            (globalWebSocket.readyState === WebSocket.OPEN ||
              globalWebSocket.readyState === WebSocket.CONNECTING)
          ) {
            logger.log(
              `[WebSocketStore] Reusing existing global WebSocket (state: ${
                globalWebSocket.readyState === WebSocket.OPEN
                  ? "OPEN"
                  : "CONNECTING"
              }, sessionId: ${globalSessionId})`
            );
            set({
              ws: globalWebSocket,
              sessionId: globalSessionId,
              connectionStatus:
                globalWebSocket.readyState === WebSocket.OPEN
                  ? "Connected"
                  : "Connecting...",
            });
            return;
          }

          // Check instance WebSocket
          if (
            ws &&
            (ws.readyState === WebSocket.OPEN ||
              ws.readyState === WebSocket.CONNECTING)
          ) {
            logger.log(
              `[WebSocketStore] WebSocket already ${
                ws.readyState === WebSocket.OPEN ? "open" : "connecting"
              }`
            );
            set({
              connectionStatus:
                ws.readyState === WebSocket.OPEN
                  ? "Connected"
                  : "Connecting...",
            });
            return;
          }

          // No existing connection - proceed with new connection
          // Clear any pending reconnect timeout
          if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
            set({ reconnectTimeout: null });
          }

          // Clear any existing heartbeat (shouldn't exist if no connection, but safety check)
          if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
            set({ heartbeatInterval: null });
          }

          // Check max reconnect attempts
          if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            logger.error("[WebSocketStore] Max reconnection attempts reached");
            set({
              connectionStatus: "Connection Failed",
              reconnectAttempts: 0, // Reset for manual retry
            });
            return;
          }

          // Generate new session ID
          const newSessionId = generateSessionId();
          globalSessionId = newSessionId;

          const url = `${config.websocketUrl}?sessionId=${newSessionId}`;
          logger.log(
            `[WebSocketStore] Creating NEW WebSocket connection (attempt ${
              reconnectAttempts + 1
            }/${MAX_RECONNECT_ATTEMPTS}) with sessionId: ${newSessionId}`
          );

          set({ connectionStatus: "Connecting...", sessionId: newSessionId });

          const newWs = new WebSocket(url);
          globalWebSocket = newWs;

          // Store WebSocket instance immediately so it's available in event handlers
          set({ ws: newWs });

          // Setup event handlers
          newWs.onopen = () => {
            logger.log(
              `[WebSocketStore] Connected | sessionId=${newSessionId}`
            );
            set({
              connectionStatus: "Connected",
              reconnectAttempts: 0, // Reset on successful connection
              reconnectDelay: BASE_RECONNECT_DELAY_MS,
            });

            // Start heartbeat
            get().startHeartbeat();
          };

          newWs.onclose = (event) => {
            logger.log(
              `[WebSocketStore] Disconnected | Code: ${event.code}, Reason: ${event.reason}, Clean: ${event.wasClean} | sessionId=${newSessionId}`
            );

            if (event.code === 1006) {
              logger.warn(
                "[WebSocketStore] Abnormal closure (1006). Common when tab is idle/background, causing connection timeout."
              );
            }

            set({ connectionStatus: "Disconnected" });
            get().stopHeartbeat();

            // Clear global reference if this was the global connection
            if (globalWebSocket === newWs) {
              logger.log(
                "[WebSocketStore] Clearing global WebSocket reference"
              );
              globalWebSocket = null;
              globalSessionId = null;
            }

            // Auto-reconnect if enabled and tab is visible
            const { isAutoReconnectEnabled: autoReconnect, isTabVisible } =
              get();
            if (autoReconnect && isTabVisible) {
              const delay = get().getReconnectDelay();
              logger.log(
                `[WebSocketStore] Scheduling reconnect in ${delay}ms (attempt ${
                  get().reconnectAttempts + 1
                })`
              );

              const timeout = setTimeout(() => {
                set((state) => ({
                  reconnectAttempts: state.reconnectAttempts + 1,
                }));
                get().connect();
              }, delay);

              set({ reconnectTimeout: timeout, reconnectDelay: delay });
            }
          };

          newWs.onerror = (error) => {
            logger.error(
              `[WebSocketStore] Error: ${error.message} | sessionId=${newSessionId}`
            );
            set({ connectionStatus: "Connection Error" });
            get().stopHeartbeat();

            // Clear global reference if this was the global connection
            if (globalWebSocket === newWs) {
              logger.log(
                "[WebSocketStore] Clearing global WebSocket reference (error)"
              );
              globalWebSocket = null;
              globalSessionId = null;
            }
            // onclose will be called automatically after error
          };

          newWs.onmessage = (event) => {
            try {
              let message = JSON.parse(event.data);

              // Handle SNS-wrapped messages (Message property contains stringified event)
              if (message.Message && typeof message.Message === "string") {
                try {
                  message = JSON.parse(message.Message);
                } catch (e) {
                  logger.error(
                    "[WebSocketStore] Failed to parse SNS Message:",
                    e
                  );
                  return;
                }
              }

              // Handle pong response
              if (message.eventType === "pong") {
                logger.log(
                  `[WebSocketStore] Pong received | sessionId=${message.sessionId} | clientTime=${message.clientTimestamp} | serverTime=${message.serverTimestamp}`
                );
                return;
              }

              // Route message to handlers
              get().handleMessage(message);
            } catch (error) {
              logger.error("[WebSocketStore] Failed to parse message:", error);
            }
          };

          // WebSocket instance already set above before event handlers
        },

        /**
         * Disconnect from WebSocket
         */
        disconnect: () => {
          const { ws, reconnectTimeout } = get();

          logger.log("[WebSocketStore] Disconnecting");

          // NOTE: Do NOT modify isAutoReconnectEnabled here!
          // That's a user preference that should persist across sessions.
          // Disconnecting should only clean up the current connection state.

          // Clear reconnect timeout
          if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
            set({ reconnectTimeout: null });
          }

          // Stop heartbeat
          get().stopHeartbeat();

          // Close WebSocket
          if (ws) {
            ws.close();
            set({ ws: null, sessionId: null });
          }

          // Clear global reference
          if (globalWebSocket === ws) {
            globalWebSocket = null;
            globalSessionId = null;
          }

          set({ connectionStatus: "Disconnected", reconnectAttempts: 0 });
        },

        /**
         * Manually trigger reconnect
         */
        reconnect: () => {
          logger.log("[WebSocketStore] Manual reconnect triggered");

          // Temporarily disable auto-reconnect to prevent double-reconnection
          // (disconnect triggers onclose which would auto-reconnect if enabled)
          const { isAutoReconnectEnabled: wasAutoReconnectEnabled } = get();
          set({ isAutoReconnectEnabled: false, reconnectAttempts: 0 });

          get().disconnect();

          // Re-enable auto-reconnect if it was previously enabled
          set({ isAutoReconnectEnabled: wasAutoReconnectEnabled });

          // Manually connect
          get().connect();
        },

        // --- Heartbeat Management ---

        /**
         * Start heartbeat interval
         */
        startHeartbeat: () => {
          const { ws, sessionId, heartbeatInterval } = get();

          if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
          }

          const interval = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              const pingMessage = JSON.stringify({
                eventType: "ping",
                sessionId,
                timestamp: new Date().toISOString(),
              });
              ws.send(pingMessage);
              logger.log(`[WebSocketStore] Ping sent | sessionId=${sessionId}`);
            }
          }, HEARTBEAT_INTERVAL_MS);

          set({ heartbeatInterval: interval });
        },

        /**
         * Stop heartbeat interval
         */
        stopHeartbeat: () => {
          const { heartbeatInterval } = get();
          if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
            set({ heartbeatInterval: null });
          }
        },

        // --- Message Handling ---

        /**
         * Register message handler
         * @param {Function} handler - Message handler function (message) => void
         * @returns {Function} Unregister function
         *
         * Note: This uses a module-level array instead of store state to avoid
         * triggering re-renders when handlers are registered/unregistered.
         */
        registerMessageHandler: (handler) => {
          // Access the handlers array directly from module scope (defined below)
          if (!messageHandlersRef.current) {
            messageHandlersRef.current = [];
          }
          messageHandlersRef.current.push(handler);

          // Return unregister function
          return () => {
            if (messageHandlersRef.current) {
              messageHandlersRef.current = messageHandlersRef.current.filter(
                (h) => h !== handler
              );
            }
          };
        },

        /**
         * Handle incoming message by routing to all registered handlers
         * @param {Object} message - Parsed message object
         */
        handleMessage: (message) => {
          // Use the module-level handlers array
          const handlers = messageHandlersRef.current || [];
          handlers.forEach((handler) => {
            try {
              handler(message);
            } catch (error) {
              logger.error("[WebSocketStore] Message handler error:", error);
            }
          });
        },

        /**
         * Send message through WebSocket
         * @param {Object} message - Message object to send
         * @returns {boolean} Success status
         */
        sendMessage: (message) => {
          const { ws } = get();
          if (ws && ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify(message));
              logger.log("[WebSocketStore] Message sent:", message);
              return true;
            } catch (error) {
              logger.error("[WebSocketStore] Failed to send message:", error);
              return false;
            }
          }
          logger.warn(
            "[WebSocketStore] Cannot send message: WebSocket not open"
          );
          return false;
        },

        // --- Settings (auto-persisted via middleware) ---

        /**
         * Toggle auto-reconnect
         */
        toggleAutoReconnect: () => {
          set((state) => {
            const newValue = !state.isAutoReconnectEnabled;
            logger.log(
              `[WebSocketStore] Auto-reconnect ${
                newValue ? "enabled" : "disabled"
              }`
            );
            return { isAutoReconnectEnabled: newValue };
          });
        },

        /**
         * Set auto-reconnect enabled state
         * @param {boolean} enabled - Auto-reconnect enabled
         */
        setAutoReconnectEnabled: (enabled) => {
          set({ isAutoReconnectEnabled: enabled });
        },

        /**
         * Toggle reconnect modal visibility
         */
        toggleReconnectModal: () => {
          set((state) => {
            const newValue = !state.showReconnectModal;
            return { showReconnectModal: newValue };
          });
        },

        /**
         * Set reconnect modal visibility
         * @param {boolean} show - Show modal
         */
        setShowReconnectModal: (show) => {
          set({ showReconnectModal: show });
        },

        /**
         * Set tab visibility state
         * @param {boolean} visible - Tab is visible
         */
        setTabVisible: (visible) => {
          set({ isTabVisible: visible });
          logger.log(`[WebSocketStore] Tab visibility: ${visible}`);

          // Resume connection if tab becomes visible and disconnected
          const { connectionStatus, isAutoReconnectEnabled } = get();
          if (
            visible &&
            isAutoReconnectEnabled &&
            connectionStatus === "Disconnected"
          ) {
            logger.log("[WebSocketStore] Tab visible, resuming connection");
            get().connect();
          }
        },

        // --- Cleanup ---

        /**
         * Reset all state (for testing/debugging)
         */
        reset: () => {
          get().disconnect();
          // Clear module-level handlers
          messageHandlersRef.current = [];
          set({
            ws: null,
            sessionId: null,
            connectionStatus: "Disconnected",
            reconnectAttempts: 0,
            reconnectDelay: BASE_RECONNECT_DELAY_MS,
            heartbeatInterval: null,
            reconnectTimeout: null,
            // messageHandlers removed - using module-level ref
            // Note: isAutoReconnectEnabled and showReconnectModal are NOT reset
            // as they are user preferences persisted via middleware
          });
        },
      }),
      {
        name: "websocket-preferences",
        partialize: (state) => ({
          isAutoReconnectEnabled: state.isAutoReconnectEnabled,
          showReconnectModal: state.showReconnectModal,
        }),
      }
    ),
    { name: "WebSocketStore" }
  )
);

export default useWebSocketStore;
